import React, {useEffect, useState, useRef} from 'react';
import styled from 'styled-components';
import {linearGradient} from 'polished';
import YouTube from 'react-youtube';
import {useList, useListVals} from 'react-firebase-hooks/database';
import uid from 'uid';
import _ from 'lodash';
import {CircularProgressbar, buildStyles} from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

import Grid from "../common/Grid";
import Header from '../header';
import Footer from '../footer';
import Comment from '../comments';
import Profile from '../profile';
import PlaylistItem from '../playlist';
import spacings from '../utilities/spacings';

import videos from '../../youtube/videos.json';
import Button from "../common/Button";
import colors from "../utilities/colors";

const PlayerWrapper = styled.div`
  background: #333333;
  height: 518px;
  margin-bottom: ${spacings.l};
  
  @media (max-width: 920px) {
    height: auto;
  }
  
  .pre-container {
    max-width: 920px;
    margin: 0 auto;
  }
  
  .container {
    position: relative;
    padding-bottom: 56.25%;
  }
  
  .player {
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
  }
  
  .comments-heading {
    margin: ${spacings.m} 0px;
  }
`;

const ComposerWrapper = styled.div`
  background-color: ${colors.whiteGrey};
  padding: 20px;
  margin-bottom: ${spacings.m};
  border: 1px solid ${colors.lightGrey};

  .label {
    min-width: 180px;
    padding-right: 20px;
    font-size: 14px;
    font-weight: bold;
  }
  .display-name, .comment {
    font-family: "Helvetica Neue", sans-serif;
    border: 1px solid ${colors.lightGrey};
    padding: ${spacings.xs};
    font-size: 16px;
    width: 100%;
  }
  .comment {
    height: 100px;
  }
  
  @media (max-width: 850px) {
    .u-flex {
      display: block;
    }
    .u-text-right {
      text-align: left;
    }
  }
`;

const Placeholder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-image: url('${props => props.background}');
  background-position: center;
  background-size: cover;
  cursor: pointer;
  
  display: ${props => props.isPlaying ? 'none' : 'block'};
  
  .button-wrapper {
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    padding: ${spacings.m} ${spacings.l};
    padding-top: ${spacings.xl};
    box-sizing: border-box;
    
    .autoplay-progress {
      width: 40px;
      margin-left: 10px;
    }
  
    ${linearGradient({
    colorStops: ['#333333 0%', 'rgba(0, 0, 0, 0.65) 50%', 'rgba(0, 0, 0, 0) 100%'],
    toDirection: 'to top',
    fallback: 'rgba(0,0,0,0)',
})}
    
    h1 {
      margin: 0;
      color: ${colors.white};
      line-height: 1;
    }
    
    p {
      color: ${colors.white};
      margin-top: ${spacings.xs};
    }
    
    button {
      border-radius: 100%;
      border: none;
      background: ${colors.primary};
      color: ${colors.white};
      width: 64px;
      height: 64px;
      position: relative;
      box-shadow: 0px 1px 6px rgba(0,0,0,0.2);
      margin-right: ${spacings.m};
      cursor: pointer;
      
      .CircularProgressbar {
        margin: -12px;
      }
      
      &:after {
        content: "";
        border-left: 20px solid white;
        border-top: 15px solid transparent;
        border-bottom: 15px solid transparent;
        position: absolute;
        top: 50%;
        left: 50%;
        margin-top: -15px;
        margin-left: -6px;
      }
    }
  }
  
  @media (max-width: 850px) {
    .button-wrapper {
      padding: ${spacings.xs} ${spacings.m};
        h1 {
          font-size: 18px;
        }
        p {
          margin-bottom: ${spacings.xs};
          font-size: 14px;
        }
        button {
          width: 48px;
          height: 48px;
          &:after {
            border-bottom-width: 10px;
            border-top-width: 10px;
            border-left-width: 15px;
            margin-top: -10px;
            margin-left: -5px;
          }
        }
    }
  }
`;

const Moderation = styled.div`
  position: fixed;
  top: 5vh;
  left: 50%;
  margin-left: -320px;
  width: 640px;
  max-width: 640px;
  height: 80vh;
  background: white;
  z-index: 1;
  padding: 20px;
  box-sizing: border-box;
  border-radius: 8px;
  
  h1 {
    font-size: 18px;
  }
`;

const MoreVidsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
`;

function useInterval(callback, delay) {
    const savedCallback = useRef();

    // Remember the latest callback.
    useEffect(() => {
        savedCallback.current = callback;
    }, [callback]);

    // Set up the interval.
    useEffect(() => {
        function tick() {
            savedCallback.current();
        }

        if (delay !== null) {
            let id = setInterval(tick, delay);
            return () => clearInterval(id);
        }
    }, [delay]);
}

function Player(props) {
    const initial_video_state = {
        ...videos[0]
    };

    const {firebase, user} = props;

    const [show_moderation, toggleModeration] = useState(false);
    const [show_impersonate, toggleImpersonate] = useState(false);
    const [show_composer, toggleComposer] = useState(false);
    const [video_event, updateVideoEvent] = useState({});

    const [selected_video, selectVideo] = useState(initial_video_state);
    const [is_playing, toggleIsPlaying] = useState(false);
    const [autoplay_data, updateAutoplay] = useState({display: 0, percentage: 0, count: 0});
    const [composer_data, changeComposerData] = useState({displayName: '', comment: '', photo: ''});

    const [snapshots] = useListVals(props.firebase.database().ref('comments/'));
    const [moderators] = useListVals(props.firebase.database().ref('moderators/'));

    const player_opts = {
        width: '100%',
        height: '100%'
    };

    useInterval(function () {
        if (selected_video.id !== "seo-1" && !is_playing) {
            window.requestAnimationFrame(function () {
                if (autoplay_data.count <= 4) {
                    updateAutoplay({
                        count: autoplay_data.count + 1,
                        display: (4) - autoplay_data.count,
                        percentage: 20 * (autoplay_data.count + 1)
                    });
                } else {
                    updateAutoplay({display: 0, percentage: 0, count: 0});
                    toggleIsPlaying(true);
                    video_event.target.playVideo();
                }
            });
        }
    }, 1000);

    const loopVideos = function loopVideosFn() {
        return videos.map(function (video) {
            return <PlaylistItem key={video.video_id} onClick={() => {
                selectVideo(video);
                toggleIsPlaying(false);
                window.scrollTo(0, 0);
            }} {...video} />
        })
    };

    const mapVideoToPlayer = function mapVideoToPlayerFn(video) {
        return {
            id: video.video_id,
            videoId: video.video_id,
        }
    };

    const createComment = function createCommentFn() {
        if (
            ((composer_data.displayName !== false &&
                composer_data.displayName !== '' &&
                composer_data.displayName !== null) || user.displayName !== null) &&
            composer_data.comment !== '' &&
            composer_data.comment !== null &&
            composer_data.comment !== false
        ) {

            let username = composer_data.displayName;

            if (!show_impersonate) {
                username = user.displayName;

                if (user.displayName === null) {
                    username = composer_data.displayName;
                    firebase.auth().currentUser.updateProfile({
                        displayName: composer_data.displayName
                    });
                }
            }

            let comment_object = {
                uid: uid(),
                display_name: username,
                content: composer_data.comment,
                date_added: Date.now(),
                approved: false,
                email: user.email,
                user_id: user.uid
            };

            if (show_impersonate && composer_data.photo !== '') {
                comment_object.user_id = 'impersonated';
                comment_object.photo = composer_data.photo;
                comment_object.email = 'impersonated@seo-usa.org';
            }

            firebase.database().ref('comments/').push(comment_object);

            toggleComposer(false);
            changeComposerData({
                displayName: composer_data.displayName,
                comment: ''
            });

        } else {
            alert("All fields are required to leave a comment.");
        }
    };

    const returnModerationStatus = function returnModerationStatusFn() {
        if (moderators.indexOf(user.uid) !== -1) {
            return toggleModeration;
        }

        return false;
    };

    const getUnmoderatedComments = function getUnmoderatedCommentsFn(comments) {
        return comments.filter((comment) => comment.approved === false);
    };

    const approveRemainingComments = function approveRemainingCommentsFn() {
        let moderated = snapshots.map(e => ({...e, approved: true}));

        firebase.database().ref('comments/').set(moderated);
    };

    const removeComment = function removeCommentFn(uid) {
        let moderated = _.filter(snapshots, function (x) {
            return x.uid !== uid;
        });
        firebase.database().ref('comments/').set(moderated);
    };

    const playVideo = function playVideoFn() {
        video_event.target.playVideo();
        toggleIsPlaying(true);
    };

    const videoIsReady = function videoIsReadyFn(event) {
        updateVideoEvent(event);
    };

    const goNextVideo = function goNextVideoFn() {
        let current_id = Number(selected_video.id.split('-')[1]);
        let next_video = _.find(videos, { id: 'seo-' + ( current_id + 1 ) });

        if( typeof next_video !== "undefined" ) {
            selectVideo(next_video);
        } else {
            selectVideo(videos[0]);
        }
    };

    return (
        <div className={"App " + (show_moderation ? 'with-moderation' : '')}>
            {show_moderation ? (
                <Moderation>
                    <div className={'u-flex u-separate-contents'}>
                        <h1 className={'u-text-center'}>{getUnmoderatedComments(snapshots).length} comments pending
                            moderation</h1>
                        <Button
                            class={'btn-secondary'}
                            label={'Approve Remaining'}
                            tag={'button'}
                            onClick={() => approveRemainingComments()}
                        />
                        <Button
                            class={'btn-secondary'}
                            label={'Close'}
                            tag={'button'}
                            onClick={() => toggleModeration(false)}
                        />
                    </div>
                    <hr/>
                    <div>
                        {getUnmoderatedComments(snapshots).map(function (comment, i) {
                            return <Comment key={i} uid={comment.uid} {...comment} moderation={true}
                                            removeComment={removeComment}/>
                        })}
                        {getUnmoderatedComments(snapshots).length === 0 ?
                            <p className={'u-text-center'} style={{fontSize: 13}}>
                                There are no comments left to be moderated. Comments will appear here in real-time as
                                they are composed.
                            </p> : ''}
                    </div>
                </Moderation>
            ) : ''}
            <Header/>
            <PlayerWrapper>
                <div className={'pre-container'}>
                    <div className="container">
                        <YouTube
                            containerClassName={'player'}
                            opts={player_opts}
                            {...mapVideoToPlayer(selected_video)}
                            onReady={videoIsReady}
                            onEnd={() => {
                                toggleIsPlaying(false);
                                goNextVideo();
                            }}
                        />
                        <Placeholder isPlaying={is_playing} background={selected_video.cover_image} onClick={playVideo}>
                            <div className={'button-wrapper u-flex'}>
                                <div>
                                    <button onClick={playVideo}>
                                        <CircularProgressbar
                                            value={autoplay_data.percentage}
                                            strokeWidth={50}
                                            background={false}
                                            styles={buildStyles({
                                                strokeLinecap: "butt",
                                                pathColor: "rgba(255,255,255,0.4)",
                                                trailColor: "transparent"
                                            })}
                                        />
                                    </button>
                                </div>
                                <div>
                                    <h1>{selected_video.title}</h1>
                                    <p>{selected_video.short_description}</p>
                                </div>
                            </div>
                        </Placeholder>
                    </div>
                </div>
            </PlayerWrapper>
            <Grid.Wrapper>
                <Grid.Row>
                    <Grid.Col class={'two-thirds'}>
                        <h1 className={"h1 u-text-center"}>
                            Welcome to the SEO 2020 Stay at Home Awards Dinner
                        </h1>
                        <p className={'u-text-center'}>
                            We are thrilled you could join us for the 2020 Stay at Home Awards Dinner, an online
                            experience to honor outstanding business and philanthropic leaders, celebrate SEO students
                            and alumni, and raise support for SEO’s high-impact programs.
                        </p>
                        <p className={'u-text-center'}>
                            <b>Please visit here often – we'll be adding new content through early May.</b>
                        </p>
                        <div className={'u-flex u-separate-contents u-margin-vertical-l u-align-items-baseline'}>
                            <h2 className="h2">See next</h2>
                        </div>
                        <MoreVidsWrapper>
                            {loopVideos()}
                        </MoreVidsWrapper>
                    </Grid.Col>
                    <Grid.Col class={'one-third'}>
                        <Profile user={props.user}
                                 logout={props.logout}
                                 toggleModeration={returnModerationStatus()}
                                 toggleComposer={toggleComposer}
                                 toggleImpersonate={toggleImpersonate}
                        />
                    </Grid.Col>
                </Grid.Row>
            </Grid.Wrapper>
            <Footer/>
        </div>
    );
}

export default Player;
